import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Search, Filter, Eye, Download, Truck, Package, Clock, CheckCircle, XCircle, RotateCcw, Calendar } from 'lucide-react';
interface OrderManagementProps {
  onNavigate: (page: string) => void;
}
export function OrderManagement({
  onNavigate
}: OrderManagementProps) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const orders = [{
    id: 'PO-2023-001',
    date: '10 Okt 2023',
    items: 32,
    total: 4500000,
    status: 'in_transit',
    statusLabel: 'Dalam Pengiriman',
    eta: 'Besok, 14:00 WIB',
    courier: 'JNE Trucking',
    trackingNumber: 'JNE88291002',
    progress: 75
  }, {
    id: 'PO-2023-002',
    date: '8 Okt 2023',
    items: 18,
    total: 2100000,
    status: 'delivered',
    statusLabel: 'Terkirim',
    deliveredDate: '9 Okt 2023',
    courier: 'SiCepat Cargo',
    trackingNumber: 'SC12345678',
    progress: 100
  }, {
    id: 'PO-2023-003',
    date: '5 Okt 2023',
    items: 45,
    total: 6800000,
    status: 'delivered',
    statusLabel: 'Terkirim',
    deliveredDate: '7 Okt 2023',
    courier: 'JNE Trucking',
    trackingNumber: 'JNE88291003',
    progress: 100
  }, {
    id: 'PO-2023-004',
    date: '3 Okt 2023',
    items: 28,
    total: 3200000,
    status: 'processing',
    statusLabel: 'Diproses',
    eta: '5 Okt 2023',
    courier: '-',
    trackingNumber: '-',
    progress: 25
  }, {
    id: 'PO-2023-005',
    date: '1 Okt 2023',
    items: 15,
    total: 1800000,
    status: 'cancelled',
    statusLabel: 'Dibatalkan',
    cancelReason: 'Stok tidak tersedia',
    courier: '-',
    trackingNumber: '-',
    progress: 0
  }];
  const stats = [{
    label: 'Total Pesanan',
    value: '156',
    icon: Package,
    color: 'blue'
  }, {
    label: 'Dalam Proses',
    value: '8',
    icon: Clock,
    color: 'amber'
  }, {
    label: 'Dalam Pengiriman',
    value: '12',
    icon: Truck,
    color: 'violet'
  }, {
    label: 'Terkirim',
    value: '136',
    icon: CheckCircle,
    color: 'green'
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <Clock className="w-4 h-4" data-id="element-2792" />;
      case 'in_transit':
        return <Truck className="w-4 h-4" data-id="element-2793" />;
      case 'delivered':
        return <CheckCircle className="w-4 h-4" data-id="element-2794" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" data-id="element-2795" />;
      default:
        return <Package className="w-4 h-4" data-id="element-2796" />;
    }
  };
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'processing':
        return 'warning';
      case 'in_transit':
        return 'warning';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'neutral';
      default:
        return 'neutral';
    }
  };
  return <div className="space-y-6" data-id="element-2797">
      {/* Header */}
      <div data-id="element-2798">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-2799">Manajemen Pesanan</h2>
        <p className="text-sm text-slate-500 mt-1" data-id="element-2800">
          Monitor dan kelola semua purchase order Anda
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4" data-id="element-2801">
        {stats.map((stat, i) => {
        const Icon = stat.icon;
        return <Card key={i} className={`p-4 border-l-4 border-l-${stat.color}-500`} data-id="element-2802">
              <div className="flex items-center gap-3 mb-2" data-id="element-2803">
                <div className={`w-10 h-10 bg-${stat.color}-100 rounded-lg flex items-center justify-center`} data-id="element-2804">
                  <Icon className={`w-5 h-5 text-${stat.color}-600`} data-id="element-2805" />
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-1" data-id="element-2806">{stat.label}</p>
              <p className="text-3xl font-bold text-slate-900" data-id="element-2807">{stat.value}</p>
            </Card>;
      })}
      </div>

      {/* Filters */}
      <div className="flex gap-3" data-id="element-2808">
        <div className="relative flex-1" data-id="element-2809">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2810" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cari nomor PO, tanggal, atau status..." className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" data-id="element-2811" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" data-id="element-2812">
          <option value="all" data-id="element-2813">Semua Status</option>
          <option value="processing" data-id="element-2814">Diproses</option>
          <option value="in_transit" data-id="element-2815">Dalam Pengiriman</option>
          <option value="delivered" data-id="element-2816">Terkirim</option>
          <option value="cancelled" data-id="element-2817">Dibatalkan</option>
        </select>
        <button className="px-4 py-3 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-2" data-id="element-2818">
          <Filter className="w-5 h-5 text-slate-600" data-id="element-2819" />
          <span className="font-medium text-slate-700" data-id="element-2820">Filter</span>
        </button>
      </div>

      {/* Orders List */}
      <div className="space-y-4" data-id="element-2821">
        {orders.map(order => <Card key={order.id} className="p-6 hover:shadow-lg transition-shadow" data-id="element-2822">
            <div className="flex items-start justify-between mb-4" data-id="element-2823">
              <div className="flex-1" data-id="element-2824">
                <div className="flex items-center gap-3 mb-2" data-id="element-2825">
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-2826">
                    {order.id}
                  </h3>
                  <Badge variant={getStatusVariant(order.status)} className="flex items-center gap-1" data-id="element-2827">
                    {getStatusIcon(order.status)}
                    {order.statusLabel}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-600" data-id="element-2828">
                  <div className="flex items-center gap-1" data-id="element-2829">
                    <Calendar className="w-4 h-4" data-id="element-2830" />
                    <span data-id="element-2831">Dibuat: {order.date}</span>
                  </div>
                  {order.status === 'in_transit' && <div className="flex items-center gap-1 text-blue-600 font-medium" data-id="element-2832">
                      <Truck className="w-4 h-4" data-id="element-2833" />
                      <span data-id="element-2834">ETA: {order.eta}</span>
                    </div>}
                  {order.status === 'delivered' && <div className="flex items-center gap-1 text-green-600 font-medium" data-id="element-2835">
                      <CheckCircle className="w-4 h-4" data-id="element-2836" />
                      <span data-id="element-2837">Diterima: {order.deliveredDate}</span>
                    </div>}
                </div>
              </div>

              <div className="text-right" data-id="element-2838">
                <p className="text-sm text-slate-500 mb-1" data-id="element-2839">Total Nilai</p>
                <p className="text-2xl font-bold text-blue-600" data-id="element-2840">
                  Rp {order.total.toLocaleString('id-ID')}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            {order.status !== 'cancelled' && <div className="mb-4" data-id="element-2841">
                <div className="flex justify-between items-center mb-2" data-id="element-2842">
                  <span className="text-xs font-medium text-slate-600" data-id="element-2843">
                    Progress Pengiriman
                  </span>
                  <span className="text-xs font-bold text-slate-900" data-id="element-2844">
                    {order.progress}%
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2845">
                  <div className={`h-full rounded-full transition-all duration-500 ${order.status === 'delivered' ? 'bg-gradient-to-r from-green-500 to-green-600' : order.status === 'in_transit' ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gradient-to-r from-amber-500 to-amber-600'}`} style={{
              width: `${order.progress}%`
            }} data-id="element-2846"></div>
                </div>
              </div>}

            {/* Details */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-2847">
              <div className="flex items-center gap-6 text-sm" data-id="element-2848">
                <span className="text-slate-600" data-id="element-2849">{order.items} Item</span>
                {order.courier !== '-' && <>
                    <span className="text-slate-400" data-id="element-2850">•</span>
                    <span className="text-slate-600" data-id="element-2851">
                      Kurir: {order.courier}
                    </span>
                  </>}
                {order.trackingNumber !== '-' && <>
                    <span className="text-slate-400" data-id="element-2852">•</span>
                    <span className="text-slate-600 font-mono text-xs" data-id="element-2853">
                      Resi: {order.trackingNumber}
                    </span>
                  </>}
              </div>

              <div className="flex gap-2" data-id="element-2854">
                {order.status === 'delivered' && <Button variant="outline" size="sm" icon={<RotateCcw className="w-4 h-4" data-id="element-2856" />} data-id="element-2855">
                    Pesan Ulang
                  </Button>}
                <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-2858" />} data-id="element-2857">
                  PDF
                </Button>
                <Button variant={order.status === 'in_transit' ? 'primary' : 'outline'} size="sm" icon={<Eye className="w-4 h-4" data-id="element-2860" />} onClick={() => onNavigate('tracking')} data-id="element-2859">
                  {order.status === 'in_transit' ? 'Lacak' : 'Detail'}
                </Button>
              </div>
            </div>

            {/* Cancel Reason */}
            {order.status === 'cancelled' && order.cancelReason && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2861">
                <p className="text-sm text-red-800" data-id="element-2862">
                  <span className="font-bold" data-id="element-2863">Alasan Pembatalan:</span>{' '}
                  {order.cancelReason}
                </p>
              </div>}
          </Card>)}
      </div>
    </div>;
}